// frontend/src/pages/Profile/components/HostexIntegration.tsx
import React, { useState, useEffect } from 'react';
import {
  Stack,
  Button,
  Alert,
  Divider,
  Title,
  Text,
  Center,
  Loader,
  Progress,
  Group,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconCheck,
  IconTrash,
  IconRefresh,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  integrationsApi,
  HostexProperty,
  MyHostexProperty,
} from '../../../api/integrations.api';
import HostexPropertySelector from './HostexPropertySelector';

interface Props {
  onClose: () => void;
}

const HostexIntegration: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [integration, setIntegration] = useState<any>(null);
  const [hostexProperties, setHostexProperties] = useState<HostexProperty[]>([]);
  const [myProperties, setMyProperties] = useState<MyHostexProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const response = await integrationsApi.getIntegration('hostex');
      if (response.data.success && response.data.data) {
        setIntegration(response.data.data);
        if (response.data.data.is_verified) {
          await loadProperties();
          return;
        }
      }
    } catch (e) {
      // запись ещё не создана — ОК, верифицируем при первой загрузке
    } finally {
      setLoading(false);
    }
    // Авто-верификация при первом открытии: токен задан в .env, поэтому
    // запрос без api_key_v1 проверит env-токен и создаст запись.
    await handleVerify();
  };

  const loadProperties = async () => {
    try {
      setLoadingProperties(true);
      setLoadingProgress(0);
      setLoadingMessage(t('integrations.hostex.loadingData'));

      setLoadingProgress(20);
      const myResp = await integrationsApi.getMyHostexProperties();
      if (myResp.data.success) setMyProperties(myResp.data.data);

      setLoadingProgress(50);
      setLoadingMessage(t('integrations.hostex.loadingHostexProperties'));
      const hostexResp = await integrationsApi.getHostexProperties();
      if (hostexResp.data.success) setHostexProperties(hostexResp.data.data);

      setLoadingProgress(100);
      setLoadingMessage(t('integrations.hostex.loadingComplete'));
      setTimeout(() => {
        setLoadingProgress(0);
        setLoadingMessage('');
      }, 800);
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message:
          error.response?.data?.message || t('integrations.hostex.errorLoadingProperties'),
        color: 'red',
        icon: <IconX size={18} />,
      });
      setLoadingProgress(0);
      setLoadingMessage('');
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleVerify = async () => {
    try {
      setVerifying(true);
      setLoadingProgress(30);
      setLoadingMessage(t('integrations.hostex.verifying'));
      const response = await integrationsApi.verifyHostex();
      setLoadingProgress(80);
      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('integrations.hostex.verified'),
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        setIntegration({ ...integration, is_verified: true });
        await loadProperties();
      } else {
        notifications.show({
          title: t('errors.generic'),
          message: response.data.message || t('integrations.hostex.verifyFailed'),
          color: 'red',
          icon: <IconX size={18} />,
        });
      }
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message:
          error.response?.data?.message || t('integrations.hostex.verifyFailed'),
        color: 'red',
        icon: <IconX size={18} />,
      });
    } finally {
      setVerifying(false);
      setLoadingProgress(0);
      setLoadingMessage('');
    }
  };

  const handleLink = async (propertyId: number, hostexPropertyId: number) => {
    try {
      const response = await integrationsApi.linkHostex({
        property_id: propertyId,
        hostex_property_id: hostexPropertyId,
      });
      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('integrations.hostex.linked'),
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        loadProperties();
      }
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('common.error'),
        color: 'red',
        icon: <IconX size={18} />,
      });
    }
  };

  const handleUnlink = async (propertyId: number) => {
    try {
      const response = await integrationsApi.unlinkHostex(propertyId);
      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('integrations.hostex.unlinked'),
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        loadProperties();
      }
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('common.error'),
        color: 'red',
        icon: <IconX size={18} />,
      });
    }
  };

  const handleTriggerSync = async (propertyId: number) => {
    try {
      const response = await integrationsApi.triggerHostexSync(propertyId);
      if (response.data.success) {
        notifications.show({
          title: t('common.success'),
          message: t('integrations.hostex.syncedNow'),
          color: 'green',
          icon: <IconCheck size={18} />,
        });
      } else {
        notifications.show({
          title: t('errors.generic'),
          message: response.data.message || t('common.error'),
          color: 'red',
          icon: <IconX size={18} />,
        });
      }
    } catch (error: any) {
      notifications.show({
        title: t('errors.generic'),
        message: error.response?.data?.message || t('common.error'),
        color: 'red',
        icon: <IconX size={18} />,
      });
    }
  };

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t('integrations.hostex.deleteConfirmTitle'),
      children: <Text size="sm">{t('integrations.hostex.deleteConfirmContent')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await integrationsApi.deleteIntegration('hostex');
          notifications.show({
            title: t('common.success'),
            message: t('integrations.hostex.deleted'),
            color: 'green',
            icon: <IconCheck size={18} />,
          });
          onClose();
        } catch (error: any) {
          notifications.show({
            title: t('errors.generic'),
            message: error.response?.data?.message || t('common.error'),
            color: 'red',
            icon: <IconX size={18} />,
          });
        }
      },
    });
  };

  if (loading) {
    return (
      <Center py={60}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            {t('common.loading')}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="xl">
      {(verifying || loadingProperties) && loadingProgress > 0 && (
        <Stack gap="xs">
          <Progress value={loadingProgress} animated color="grape" />
          {loadingMessage && (
            <Text size="sm" c="dimmed" ta="center">
              {loadingMessage}
            </Text>
          )}
        </Stack>
      )}

      <Alert
        icon={<IconInfoCircle size={18} />}
        title={t('integrations.hostex.companyTokenTitle')}
        color="grape"
      >
        <Text size="sm">{t('integrations.hostex.companyTokenInfo')}</Text>
      </Alert>

      <div>
        <Title order={5}>{t('integrations.hostex.linkProperties')}</Title>
        <Text size="sm" c="dimmed" mt={4}>
          {t('integrations.hostex.linkPropertiesDescription')}
        </Text>
      </div>

      {loadingProperties && loadingProgress === 0 ? (
        <Center py={60}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              {t('common.loading')}
            </Text>
          </Stack>
        </Center>
      ) : (
        <HostexPropertySelector
          hostexProperties={hostexProperties}
          myProperties={myProperties}
          onLink={handleLink}
          onUnlink={handleUnlink}
          onTriggerSync={handleTriggerSync}
        />
      )}

      <Divider />

      <Group gap="xs" wrap="wrap">
        <Button
          variant="light"
          leftSection={<IconRefresh size={18} />}
          onClick={loadProperties}
          loading={loadingProperties}
          disabled={loadingProperties}
          size={isMobile ? 'sm' : 'md'}
        >
          {t('common.refresh')}
        </Button>

        <Button
          variant="light"
          onClick={handleVerify}
          loading={verifying}
          disabled={verifying}
          size={isMobile ? 'sm' : 'md'}
        >
          {t('integrations.hostex.reverify')}
        </Button>

        <Button variant="light" onClick={onClose} size={isMobile ? 'sm' : 'md'}>
          {t('common.cancel')}
        </Button>

        {integration && (
          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={18} />}
            onClick={handleDelete}
            size={isMobile ? 'sm' : 'md'}
          >
            {t('integrations.hostex.deleteIntegration')}
          </Button>
        )}
      </Group>
    </Stack>
  );
};

export default HostexIntegration;
